/**
 * Create Reel Page - Government Portal Style
 * Fast & Lightweight with React.memo
 */

import React, { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api, { getApiErrorMessage, UPLOAD_TIMEOUT } from '@/services/api'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { X, Upload, Video, Loader2 } from 'lucide-react'

// ================================================================================
// VISIBILITY OPTIONS - Memoized
// ================================================================================
const VISIBILITY_OPTIONS = ['PUBLIC', 'FRIENDS', 'PRIVATE'] as const

const CreateReelPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [videoPreview, setVideoPreview] = useState<string | null>(null)
  const [caption, setCaption] = useState('')
  const [musicTitle, setMusicTitle] = useState('')
  const [musicArtist, setMusicArtist] = useState('')
  const [visibility, setVisibility] = useState<typeof VISIBILITY_OPTIONS[number]>('PUBLIC')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [hashtags, setHashtags] = useState<string[]>([])
  const [hashtagInput, setHashtagInput] = useState('')

  const addHashtag = useCallback(() => {
    const raw = hashtagInput.trim()
    if (!raw) return
    const tag = (raw.startsWith('#') ? raw.slice(1) : raw).toLowerCase()
    if (tag && !hashtags.includes(tag)) {
      setHashtags(prev => [...prev, tag])
    }
    setHashtagInput('')
  }, [hashtagInput, hashtags])

  const removeHashtag = useCallback((tag: string) => {
    setHashtags(prev => prev.filter(t => t !== tag))
  }, [])

  // Redirect if not authenticated
  if (!isAuthenticated) {
    navigate('/login')
    return null
  }

  // ================================================================================
  // HANDLERS - useCallback for performance
  // ================================================================================
  const handleVideoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('video/')) {
      alert('Please select a video file.')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(1)
      alert(`This video is ${mb} MB. The maximum allowed size is 20 MB. Please pick a smaller or shorter video.`)
      return
    }

    setVideoFile(file)
    const url = URL.createObjectURL(file)
    setVideoPreview(url)
  }, [])

  const removeVideo = useCallback(() => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview)
    }
    setVideoFile(null)
    setVideoPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [videoPreview])

  const handleSubmit = useCallback(async () => {
    if (!videoFile) {
      alert('Please select a video')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      formData.append('file', videoFile)
      formData.append('type', 'video')

      setUploadProgress(30)
      const uploadResponse = await api.post('/media/upload', formData, {
        timeout: UPLOAD_TIMEOUT,
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const videoUrl = uploadResponse.data.data.url
      setUploadProgress(70)

      await api.post('/reels', {
        videoUrl,
        caption: caption || undefined,
        musicTitle: musicTitle || undefined,
        musicArtist: musicArtist || undefined,
        visibility,
        hashtags: hashtags.length > 0 ? hashtags : undefined
      })

      setUploadProgress(100)
      navigate('/reels')
    } catch (error) {
      console.error('Error creating reel:', error)
      alert(getApiErrorMessage(error, 'Failed to create reel.'))
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }, [videoFile, caption, musicTitle, musicArtist, visibility, hashtags, navigate])

  const handleCancel = useCallback(() => {
    navigate('/reels')
  }, [navigate])

  // ================================================================================
  // RENDER
  // ================================================================================
  return (
    <GovLayout showSidebar={false}>
      <div className="max-w-2xl mx-auto">
        <GovPageTitle
          title="Create Reel"
          subtitle="Share your health awareness video with the community"
          icon={Video}
        />

        <GovCard className="p-6">
          {/* Video Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video <span className="text-red-500">*</span>
            </label>

            {videoPreview ? (
              <div className="relative aspect-[9/16] max-h-96 bg-black rounded-xl overflow-hidden">
                <video
                  src={videoPreview}
                  className="w-full h-full object-contain"
                  controls
                />
                <button
                  onClick={removeVideo}
                  className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  aria-label="Remove video"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="aspect-[9/16] max-h-96 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-colors"
              >
                <Video className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-gray-500 text-center px-4">
                  Click to upload a video
                  <br />
                  <span className="text-xs">MP4, MOV up to 20MB</span>
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoSelect}
              className="hidden"
            />
          </div>

          {/* Caption */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Caption
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption for your reel..."
              rows={3}
              maxLength={2000}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <p className="text-xs text-gray-500 text-right mt-1">
              {caption.length}/2000
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Tip: use <code className="text-blue-600">#tag</code> in the caption — it's parsed automatically. Or add tags below.
            </p>
          </div>

          {/* Hashtags */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hashtags <span className="text-gray-400">(Optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {hashtags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                  #{tag}
                  <button type="button" onClick={() => removeHashtag(tag)} className="hover:text-blue-800">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    addHashtag()
                  }
                }}
                placeholder="Add hashtag (press Enter)"
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button type="button" onClick={addHashtag} className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium hover:bg-blue-100">
                Add
              </button>
            </div>
          </div>

          {/* Music Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Music Title <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="text"
                value={musicTitle}
                onChange={(e) => setMusicTitle(e.target.value)}
                placeholder="Song name"
                maxLength={200}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Artist <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="text"
                value={musicArtist}
                onChange={(e) => setMusicArtist(e.target.value)}
                placeholder="Artist name"
                maxLength={200}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Visibility */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visibility
            </label>
            <div className="flex gap-3">
              {VISIBILITY_OPTIONS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVisibility(v)}
                  className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                    visibility === v
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {v.charAt(0) + v.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Uploading...</span>
                <span className="text-blue-600 font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <GovButton
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isUploading}
              className="flex-1"
            >
              Cancel
            </GovButton>
            <GovButton
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={!videoFile || isUploading}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Create Reel
                </>
              )}
            </GovButton>
          </div>
        </GovCard>
      </div>
    </GovLayout>
  )
}

export default React.memo(CreateReelPage)